import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shell, PageHeader } from '../components/Shell.jsx';
import { Score } from '../components/Score.jsx';
import { Empty } from '../components/Empty.jsx';

export default function ApprovalQueue() {
  const [pitches, setPitches] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetch('/api/pitches?status=drafted&limit=200', { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setPitches(d.pitches || []))
      .catch(() => { setErr('failed to load queue'); setPitches([]); });
  }, []);

  return (
    <Shell>
      <PageHeader
        eyebrow="Daily workflow"
        title="Approval queue"
        sub="Review every draft. Approve, edit, or skip. Nothing sends without your click."
        right={
          <>
            <Link to="/prospects/new" className="btn btn-secondary btn-sm">+ Add prospect</Link>
            <span className="badge">{pitches?.length || 0} drafts</span>
          </>
        }
      />

      {pitches === null ? (
        <div className="row muted"><span className="spinner" /> Loading drafts</div>
      ) : err ? (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)' }}>
          <p style={{ color: 'var(--danger)' }}>{err}</p>
        </div>
      ) : pitches.length === 0 ? (
        <Empty
          title="Nothing to review yet"
          body="When the drafting routine finds prospects with fit score ≥ 75, drafts appear here for your one-click approval. Add prospects to start the pipeline."
          action={<Link to="/prospects/new" className="btn">+ Add a prospect</Link>}
        />
      ) : (
        <div className="stack-4">
          {pitches.map(p => <PitchCard key={p.id} pitch={p} onAction={refresh} />)}
        </div>
      )}
    </Shell>
  );

  function refresh() {
    fetch('/api/pitches?status=drafted&limit=200', { credentials: 'include' })
      .then(r => r.ok ? r.json() : { pitches: [] })
      .then(d => setPitches(d.pitches || []));
  }
}

function PitchCard({ pitch, onAction }) {
  const [busy, setBusy] = useState(null);

  async function approve() {
    if (!confirm('Approve this pitch? It will queue for the next send window.')) return;
    setBusy('approve');
    try {
      const r = await fetch(`/api/pitches/${pitch.id}/approve`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || 'approve failed');
      const d = await r.json();
      if (d.sent) {
        if (d.test_redirect) {
          alert(`✓ Approved and sent.\n\nTest mode: redirected to ${d.effective_to} instead of ${d.intended_to}. Clear TEST_SEND_OVERRIDE_TO env when ready for real sends.`);
        } else {
          alert(`✓ Approved and sent to ${d.intended_to}`);
        }
      } else if (d.message) {
        alert(d.message);
      }
      onAction?.();
    } catch (e) {
      alert(e.message);
    } finally { setBusy(null); }
  }

  async function skip() {
    const reason = prompt('Why are you skipping this pitch? (optional, helps the system learn)');
    if (reason === null) return;
    setBusy('skip');
    try {
      const r = await fetch(`/api/pitches/${pitch.id}/skip`, {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || 'skip failed');
      onAction?.();
    } catch (e) {
      alert(e.message);
    } finally { setBusy(null); }
  }

  return (
    <article className="pitch-card fade-in">
      <div className="pitch-card-head">
        <div>
          <div className="pitch-prospect-name">{pitch.prospect_name}</div>
          <div className="pitch-prospect-meta">
            {[pitch.prospect_role, pitch.prospect_outlet].filter(Boolean).join(' · ')}
          </div>
          <div className="pitch-prospect-beat">
            {pitch.story_angle ? <span className="chip">{pitch.story_angle}</span> : null}
            {pitch.is_followup ? <span className="badge badge-warning">Follow-up</span> : null}
            {pitch.regeneration_count > 0 ? <span className="badge">Regen ×{pitch.regeneration_count}</span> : null}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Score value={pitch.fit_score} />
          <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
            Drafted {timeAgo(pitch.drafted_at)}
          </div>
        </div>
      </div>

      <div className="pitch-section">
        <div className="label" style={{ marginBottom: 4 }}>Subject</div>
        <div className="pitch-subject">{pitch.subject}</div>
      </div>

      <div className="pitch-section" style={{ paddingBottom: 'var(--space-4)' }}>
        <div className="label" style={{ marginBottom: 4 }}>Body</div>
        <div className="pitch-body">{pitch.body}</div>
      </div>

      <div className="pitch-actions">
        <button className="btn" onClick={approve} disabled={busy != null}>
          {busy === 'approve' ? <><span className="spinner" /> Approving</> : 'Approve and queue send'}
        </button>
        <Link to={`/pitches/${pitch.id}/edit`} className="btn btn-secondary">Edit</Link>
        <button className="btn btn-secondary" disabled title="Coming next: regenerate via Claude">Regenerate</button>
        <button className="btn btn-danger" onClick={skip} disabled={busy != null} style={{ marginLeft: 'auto' }}>
          {busy === 'skip' ? <><span className="spinner" /> Skipping</> : 'Skip'}
        </button>
      </div>
    </article>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
